import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";
import {
  correctSextant,
  formatDegMin,
  type Planet,
  type SunLimb,
} from "@nav-games/astro";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface CorrectionsProps {
  onNavigate: (to: string) => void;
}

const MODULE = "astro";
const GAME = "corrections";
const CARDS_PER_SESSION = 10;
const CHOICES_PER_CARD = 4;

type BodyKind = "sun" | "moon" | Planet | "star";

interface Card {
  bodyKind: BodyKind;
  bodyLabel: string;
  /// Only meaningful for Sun and Moon.
  limb: SunLimb | null;
  hsDeg: number;
  ieMin: number;
  heightOfEyeM: number;
  date: Date;
  hoDeg: number;
  hoStr: string;
  working: readonly string[];
  choices: readonly string[];
}

function randInt(lo: number, hi: number): number {
  return lo + Math.floor(Math.random() * (hi - lo + 1));
}

function randChoice<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]!;
}

function fmt(deg: number): string {
  return formatDegMin(deg, 1);
}

const BODY_KINDS: readonly BodyKind[] = [
  "sun",
  "moon",
  "venus",
  "mars",
  "jupiter",
  "saturn",
  "star",
];

function bodyLabel(kind: BodyKind, limb: SunLimb | null): string {
  const limbText = limb === "LL" ? " (lower-limb)" : limb === "UL" ? " (upper-limb)" : "";
  switch (kind) {
    case "sun":
      return `Sun${limbText}`;
    case "moon":
      return `Moon${limbText}`;
    case "star":
      return "Star";
    default:
      return kind[0]!.toUpperCase() + kind.slice(1);
  }
}

function buildCard(): Card {
  const bodyKind = randChoice(BODY_KINDS);
  const limb: SunLimb | null =
    bodyKind === "sun" || bodyKind === "moon"
      ? randChoice(["LL", "UL"] as const)
      : null;
  const hsDeg = randInt(20, 70) + randInt(0, 59) / 60;
  const ieMin = randInt(-30, 30) / 10;
  const heightOfEyeM = randInt(2, 12);
  // Today, noon UT — well-defined for Moon/planet computations and
  // visible to the player in the reveal if they want to repro.
  const date = new Date();

  // Build the body arg in the package's union.
  const bodyArg =
    bodyKind === "sun" || bodyKind === "moon"
      ? ({ kind: bodyKind, limb: limb! } as const)
      : bodyKind === "star"
        ? ({ kind: "star" } as const)
        : ({ kind: "planet", planet: bodyKind } as const);

  const r = correctSextant({
    body: bodyArg,
    hsDeg,
    indexErrorMin: ieMin,
    heightOfEyeM,
    date,
  });
  const hoStr = fmt(r.hoDeg);

  // Distractor candidates tuned to the canonical mistakes for each
  // body family.
  const candidates: number[] = [
    r.hoDeg + r.dipMin / 60, // forgot dip
    r.hoDeg + (2 * ieMin) / 60, // IE applied the wrong way
  ];
  if (bodyKind === "sun" || bodyKind === "moon") {
    const sd = r.semiDiameterMin;
    const sdSigned = limb === "LL" ? sd : -sd;
    candidates.push(
      r.hoDeg - sdSigned / 60, // forgot SD
      r.hoDeg - (2 * sdSigned) / 60, // wrong limb (SD opposite sign)
    );
  }
  if (bodyKind === "moon") {
    candidates.push(r.hoDeg - r.parallaxMin / 60); // forgot HP·cos(Ha)
  }
  if (bodyKind !== "sun" && bodyKind !== "moon") {
    // Point-source distractor — students sometimes try to add SD for
    // a planet or star out of habit.
    candidates.push(r.hoDeg + 15.994 / 60);
  }

  const distractors: string[] = [];
  const seen = new Set<string>([hoStr]);
  for (const cand of candidates) {
    if (distractors.length >= CHOICES_PER_CARD - 1) break;
    const s = fmt(cand);
    if (!seen.has(s)) {
      seen.add(s);
      distractors.push(s);
    }
  }
  let off = 5;
  while (distractors.length < CHOICES_PER_CARD - 1) {
    for (const cand of [r.hoDeg + off / 60, r.hoDeg - off / 60]) {
      if (distractors.length >= CHOICES_PER_CARD - 1) break;
      const s = fmt(cand);
      if (!seen.has(s)) {
        seen.add(s);
        distractors.push(s);
      }
    }
    off += 3;
  }

  const choices = [hoStr, ...distractors];
  for (let i = choices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = choices[i]!;
    choices[i] = choices[j]!;
    choices[j] = a;
  }

  const working = r.steps.map(
    (s) =>
      `${s.label}: ${fmt(s.fromDeg)} ${s.deltaMin >= 0 ? "+" : "−"} ${Math.abs(s.deltaMin).toFixed(1)}′ = ${fmt(s.toDeg)}`,
  );

  return {
    bodyKind,
    bodyLabel: bodyLabel(bodyKind, limb),
    limb,
    hsDeg,
    ieMin,
    heightOfEyeM,
    date,
    hoDeg: r.hoDeg,
    hoStr,
    working,
    choices,
  };
}

export function Corrections({ onNavigate }: CorrectionsProps) {
  const recorder = useMemo(
    () => new SessionRecorder({ module: MODULE, game: GAME, length: CARDS_PER_SESSION }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => buildCard());
  const [shownAt, setShownAt] = useState<number>(() => Date.now());
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef<number>(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(buildCard());
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder]);

  const onChoose = useCallback(
    (choice: string) => {
      if (chosen || result) return;
      const ok = choice === card.hoStr;
      const attempt: Omit<Attempt, "module"> = {
        char: card.bodyKind,
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(choice);
    },
    [card, chosen, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(buildCard());
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/astro")}
      />
    );
  }

  const revealed = chosen !== null;
  const iePrefix = card.ieMin >= 0 ? "+" : "−";

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "astro" : null} onClose={() => setHelpOpen(false)} />
      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION}
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>{card.bodyLabel} sight — what is Ho?</h3>
        <p style={{ marginBottom: 12 }}>
          You take a <strong>{card.bodyLabel}</strong> sight: Hs{" "}
          <strong>{fmt(card.hsDeg)}</strong>, index error{" "}
          <strong>{iePrefix}{Math.abs(card.ieMin).toFixed(1)}′</strong>{" "}
          ({card.ieMin >= 0 ? "on" : "off"} the arc), height of eye{" "}
          <strong>{card.heightOfEyeM} m</strong>.
          <br />
          What is the observed altitude Ho?
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {card.choices.map((c) => {
            const isCorrect = c === card.hoStr;
            const isChosen = c === chosen;
            return (
              <button
                key={c}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(c)}
                style={{
                  fontSize: 18,
                  padding: "14px 0",
                  fontWeight: 600,
                  background:
                    revealed && isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : revealed && isChosen
                        ? "rgba(184, 48, 42, 0.18)"
                        : undefined,
                }}
              >
                {c}
              </button>
            );
          })}
        </div>

        {revealed && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 6,
              background:
                chosen === card.hoStr
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosen === card.hoStr
                ? `✓ Correct — Ho = ${card.hoStr}.`
                : `✗ Not quite — Ho = ${card.hoStr}.`}
            </p>
            <ol style={{ margin: "8px 0 0", paddingLeft: 18 }}>
              {card.working.map((line, i) => (
                <li key={i} className="small muted" style={{ marginBottom: 2 }}>
                  {line}
                </li>
              ))}
            </ol>
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next sight →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/astro")}>
        ← Back to Astronavigation
      </button>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div className="row" style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}>
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Astronavigation
        </button>
      </div>
    </>
  );
}
